if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar()?.classList.remove('hidden');

        const ids = Object.keys(items);
        const cartNotification = document.querySelector('cart-notification');
        const cartDrawer = document.querySelector('cart-drawer');

        // cart-notification.renderContents() needs sections like cart-notification-product / button
        const cartSections = new Set(
          this.getSectionsToRender()
            .map((section) => section.section)
            .filter(Boolean)
        );
        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          cartNotification.getSectionsToRender().forEach((section) => cartSections.add(section.id));
        } else if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((section) => cartSections.add(section.id));
        }

        const body = JSON.stringify({
          updates: items,
          sections: [...cartSections],
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text())
          .then((state) => {
            let parsedState = null;
            try {
              parsedState = JSON.parse(state);
            } catch (parseErr) {
              console.error('quick-add-bulk: cart/update parse failed', parseErr);
              return;
            }

            // Prefer a line key so cart-notification can target the updated product
            if (!parsedState.key && Array.isArray(parsedState.items)) {
              const updatedIds = ids.map((id) => parseInt(id, 10));
              const matchedItem = parsedState.items.find((item) =>
                updatedIds.includes(parseInt(item.variant_id, 10))
              );
              if (matchedItem?.key) parsedState.key = matchedItem.key;
            }

            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });

            if (cartNotification && typeof cartNotification.renderContents === 'function') {
              cartNotification.renderContents(parsedState);
            } else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(parsedState);
            } else if (cartNotification && typeof cartNotification.open === 'function') {
              cartNotification.open();
            } else if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          })
          .catch((fetchErr) => {
            console.warn('quick-add-bulk: cart/update failed', fetchErr);
          })
          .finally(() => {
            this.selectProgressBar()?.classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        if (!parsedState?.sections) return;

        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            const itemsLength = Array.isArray(parsedState.items) ? parsedState.items.length : 0;
            sectionElement?.closest('cart-drawer')?.classList.toggle('is-empty', itemsLength === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            const sectionHtml = parsedState.sections?.[section.section];
            if (sectionHtml == null) return;
            elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`)?.select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (selector) {
          const targeted = doc.querySelector(selector);
          if (targeted) return targeted.innerHTML;
        }
        const sectionRoot = doc.querySelector('.shopify-section') || doc.body;
        return sectionRoot ? sectionRoot.innerHTML : '';
      }
    }
  );
}
